/**
 * The offer to install this console as an application, on a device where a
 * 28px glyph in the top bar is not an offer.
 *
 * WHY THIS EXISTS
 * ---------------
 * The console has been installable since 2026-08-22 and the offer was a single
 * icon button among six in the top bar. On a desktop that is defensible: the
 * browser is also offering it in the address bar, so the button is a shortcut
 * to something already on screen. On a phone it is not, for two separate
 * reasons that both end in nothing being offered:
 *
 *   - **Android.** `useInstallApp` calls `preventDefault()` on
 *     `beforeinstallprompt`, which is what suppresses Chrome's own offer — so
 *     the console takes the offer away from the browser and puts it back as an
 *     unlabelled 28px icon in a row that has already dropped the section name
 *     and the account name for want of width. The trade is fine on a desktop
 *     and a straight loss on a phone.
 *   - **iOS.** No WebKit browser has ever fired the event, so the button was
 *     never drawn at all and Add to Home Screen sat in the share sheet with
 *     nothing anywhere saying so.
 *
 * WHAT IT IS
 * ----------
 * A sheet at the bottom of the screen, on coarse pointers only, saying what
 * installing gets you and either doing it (Android) or naming the two taps that
 * do (iOS). Bottom rather than top because that is where a thumb is, and
 * because the top of this console is where alarms live — an install offer is
 * not an alarm and must not learn to look like one.
 *
 * It is shown once and remembered. `Attention is spent, not decorated`: an
 * offer that returns every session is a notification, and this is a
 * convenience. Dismissing it is final, and the top bar's button stays as the
 * way back — on a coarse pointer it re-opens this sheet rather than prompting
 * directly, so the capability has a permanent home and the sheet still gets to
 * explain itself.
 */

import { useCallback, useState } from 'react'
import { MonitorDown, Share, SquarePlus, X } from 'lucide-react'

import { Button } from '@/components/primitives'
import { Icon } from '@/components/icon'
import { useT } from '@/i18n/context'
import { useInstallApp, type InstallState } from '@/hooks/useInstallApp'
import { useCoarsePointer } from '@/hooks/usePointerKind'
import './InstallOffer.css'

const DISMISSED_KEY = 'kimene.install.dismissed'

/** Whether the operator has already said no. Wrapped because Safari in a
 *  private window throws on `localStorage` rather than returning null, and an
 *  install offer is not worth a blank console. */
function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

function remember(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // A device that cannot remember the dismissal offers again next time. That
    // is a mild annoyance and the alternative is a thrown error on a screen
    // somebody is working in.
  }
}

/**
 * Opens the sheet again from outside — the top bar's button on a coarse
 * pointer. Exported as a hook rather than lifted into a context because there
 * is exactly one sheet and one control, and a context for two things is a
 * context that exists to be found rather than to do anything.
 */
export function useInstallOffer(): {
  /** Whether there is anything to offer at all on this device. */
  offerable: boolean
  /** Whether the sheet should be on screen. */
  open: boolean
  show: () => void
  hide: () => void
  state: InstallState
  install: () => Promise<void>
} {
  const { state, install } = useInstallApp()
  const coarse = useCoarsePointer()
  const [asked, setAsked] = useState(false)
  const [refused, setRefused] = useState(dismissed)

  const offerable = coarse && (state === 'ready' || state === 'manual')
  const show = useCallback(() => setAsked(true), [])
  const hide = useCallback(() => {
    setAsked(false)
    setRefused(true)
    remember()
  }, [])

  return { offerable, open: offerable && (asked || !refused), show, hide, state, install }
}

interface InstallOfferProps {
  open: boolean
  onDismiss: () => void
  state: InstallState
  install: () => Promise<void>
}

export function InstallOffer({ open, onDismiss, state, install }: InstallOfferProps) {
  const t = useT()
  if (!open) return null

  const ios = state === 'manual'

  return (
    /* `role="complementary"` rather than `dialog`: a dialog takes focus and
       traps it, and this must never interrupt somebody typing into a terminal.
       It is an aside that can be ignored, and the markup should say so. */
    <aside className="k-install" role="complementary" aria-label={t('install.title')}>
      <div className="k-install__mark" aria-hidden="true">
        <Icon icon={MonitorDown} size={20} />
      </div>
      <div className="k-install__body">
        <p className="k-install__title">{t('install.title')}</p>
        <p className="k-install__why">{t('install.why')}</p>
        {ios ? (
          /* The gesture, in the order a thumb performs it. The glyphs are the
             iOS ones by name — the share box with the arrow, and the plus in a
             square — because the operator is looking for a picture in a toolbar,
             not reading a sentence. */
          <p className="k-install__steps">
            <span className="k-install__step">
              <Icon icon={Share} size={14} />
              {t('install.iosShare')}
            </span>
            <span className="k-install__arrow" aria-hidden="true">
              →
            </span>
            <span className="k-install__step">
              <Icon icon={SquarePlus} size={14} />
              {t('install.iosAdd')}
            </span>
          </p>
        ) : (
          <div className="k-install__actions">
            <Button variant="primary" size="sm" onClick={() => void install()}>
              {t('install.action')}
            </Button>
          </div>
        )}
      </div>
      <button
        type="button"
        className="k-install__close"
        onClick={onDismiss}
        aria-label={t('install.dismiss')}
        title={t('install.dismiss')}
      >
        <Icon icon={X} size={16} />
      </button>
    </aside>
  )
}
